// src/components/OrgPreview/OrgPreviewChart.js
import React, { useMemo, useCallback, useState } from 'react';
import ReactFlow, {
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { Button, Spinner, ButtonGroup } from 'react-bootstrap';
import {
    IconHierarchy2,
    IconUsers,
    IconUser,
    IconLayoutDashboard,
    IconAlertTriangle,
    IconCheck,
    IconX,
    IconLayoutDistributeVertical,
    IconLayoutDistributeHorizontal,
} from '@tabler/icons-react';

import UnitNode from './UnitNode';
import UserNode from './UserNode';
import HierarchyNode from './HierarchyNode';
import { transformToFlowData } from '../../utils/parseOrgExcel';
import { getLayoutedElements, getLayoutPreset } from '../../utils/layoutGraph';
import './OrgPreview.css';

// Define custom node types
const nodeTypes = {
    unitNode: UnitNode,
    userNode: UserNode,
    hierarchyNode: HierarchyNode,
};

/**
 * OrgPreviewChart - Displays a visual preview of the organizational structure
 * before confirming upload to the database
 * 
 * Uses Dagre for automatic node layout to prevent overlapping
 */
const OrgPreviewChart = ({
    orgData,
    onConfirm,
    onCancel,
    isConfirming = false,
    showActions = true,
    isSaved = false,
}) => {
    // Layout direction state
    const [layoutDirection, setLayoutDirection] = useState('vertical');

    // Transform org data to ReactFlow nodes and edges, then apply Dagre layout
    const { initialNodes, initialEdges, stats } = useMemo(() => {
        if (!orgData || orgData.length === 0) {
            return { initialNodes: [], initialEdges: [], stats: null };
        }

        // Get raw nodes and edges (without positions)
        const { nodes: rawNodes, edges: rawEdges, stats } = transformToFlowData(orgData);

        // Apply Dagre layout to position nodes automatically
        const layoutOptions = getLayoutPreset(layoutDirection);
        const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
            rawNodes,
            rawEdges,
            layoutOptions
        );

        return {
            initialNodes: layoutedNodes,
            initialEdges: layoutedEdges,
            stats,
        };
    }, [orgData, layoutDirection]);

    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

    // Update nodes when layout changes
    React.useEffect(() => {
        setNodes(initialNodes);
        setEdges(initialEdges);
    }, [initialNodes, initialEdges, setNodes, setEdges]);

    // Fit view on initial render
    const onInit = useCallback((reactFlowInstance) => {
        reactFlowInstance.fitView({ padding: 0.2 });
    }, []);

    // Handle layout direction change
    const handleLayoutChange = useCallback((direction) => {
        setLayoutDirection(direction);
    }, []);

    // Empty state
    if (!orgData || orgData.length === 0) {
        return (
            <div className="org-preview-container">
                <div className="org-preview-empty">
                    <IconHierarchy2 size={48} stroke={1} />
                    <h5>Keine Daten zum Anzeigen</h5>
                    <p>Bitte lade eine Excel-Datei hoch, um die Organisationsstruktur zu sehen.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="org-preview-container">
            {/* Header */}
            <div className="org-preview-header">
                <h5 className="org-preview-title">
                    <IconHierarchy2 size={20} stroke={1.5} />
                    Vorschau der Organisationsstruktur
                </h5>
                <div className="org-preview-controls">
                    <ButtonGroup size="sm">
                        <Button
                            variant={layoutDirection === 'vertical' ? 'primary' : 'outline-secondary'}
                            onClick={() => handleLayoutChange('vertical')}
                            title="Vertikales Layout"
                        >
                            <IconLayoutDistributeVertical size={16} stroke={1.5} />
                        </Button>
                        <Button
                            variant={layoutDirection === 'horizontal' ? 'primary' : 'outline-secondary'}
                            onClick={() => handleLayoutChange('horizontal')}
                            title="Horizontales Layout"
                        >
                            <IconLayoutDistributeHorizontal size={16} stroke={1.5} />
                        </Button>
                    </ButtonGroup>
                </div>
            </div>

            {/* Statistics Bar */}
            {stats && (
                <div className="org-preview-stats">
                    {stats.isHierarchical ? (
                        // Hierarchical structure stats
                        <>
                            <div className="stat-item">
                                <div className="stat-icon highlight">
                                    <IconHierarchy2 size={18} stroke={1.5} />
                                </div>
                                <div className="stat-info">
                                    <span className="stat-value">{stats.totalUnits}</span>
                                    <span className="stat-label">Organisationseinheiten</span>
                                </div>
                            </div>
                            {stats.levelCounts && Object.entries(stats.levelCounts).map(([level, count]) => (
                                <div key={level} className="stat-item">
                                    <div className="stat-icon">
                                        <IconUsers size={18} stroke={1.5} />
                                    </div>
                                    <div className="stat-info">
                                        <span className="stat-value">{count}</span>
                                        <span className="stat-label">Ebene {level}</span>
                                    </div>
                                </div>
                            ))}
                            {stats.totalUsers > 0 && (
                                <div className="stat-item">
                                    <div className="stat-icon">
                                        <IconUser size={18} stroke={1.5} />
                                    </div>
                                    <div className="stat-info">
                                        <span className="stat-value">{stats.totalUsers}</span>
                                        <span className="stat-label">Benutzer</span>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        // Flat user-based stats
                        <>
                            <div className="stat-item">
                                <div className="stat-icon highlight">
                                    <IconUser size={18} stroke={1.5} />
                                </div>
                                <div className="stat-info">
                                    <span className="stat-value">{stats.totalUsers}</span>
                                    <span className="stat-label">Benutzer insgesamt</span>
                                </div>
                            </div>
                            <div className="stat-item">
                                <div className="stat-icon highlight">
                                    <IconUsers size={18} stroke={1.5} />
                                </div>
                                <div className="stat-info">
                                    <span className="stat-value">{stats.totalUnits}</span>
                                    <span className="stat-label">Einheiten</span>
                                </div>
                            </div>
                            <div className="stat-item">
                                <div className="stat-icon">
                                    <IconLayoutDashboard size={18} stroke={1.5} />
                                </div>
                                <div className="stat-info">
                                    <span className="stat-value">{stats.usersWithDashboard}</span>
                                    <span className="stat-label">Mit Dashboard</span>
                                </div>
                            </div>
                            {stats.usersWithoutUnit > 0 && (
                                <div className="stat-item">
                                    <div className="stat-icon warning">
                                        <IconAlertTriangle size={18} stroke={1.5} />
                                    </div>
                                    <div className="stat-info">
                                        <span className="stat-value">{stats.usersWithoutUnit}</span>
                                        <span className="stat-label">Ohne Einheit</span>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Flow Chart */}
            <div className="org-flow-container">
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onInit={onInit}
                    nodeTypes={nodeTypes}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    elementsSelectable={false}
                    panOnScroll
                    zoomOnScroll
                    fitView
                    fitViewOptions={{ padding: 0.2 }}
                    minZoom={0.1}
                    maxZoom={1.5}
                >
                    <Background color="#e0e0e0" gap={20} />
                    <Controls showInteractive={false} />
                    <MiniMap
                        nodeColor={(node) => {
                            if (node.type === 'unitNode') return '#ff1628';
                            return '#69737a';
                        }}
                        maskColor="rgba(0, 0, 0, 0.1)"
                    />
                </ReactFlow>
            </div>

            {/* Action Buttons */}
            {showActions ? (
                <div className="org-preview-actions">
                <Button
                    variant="outline-secondary"
                    onClick={onCancel}
                    disabled={isConfirming}
                >
                    <IconX size={18} className="me-1" />
                    Abbrechen
                </Button>
                <Button
                    variant="primary"
                    onClick={onConfirm}
                    disabled={isConfirming}
                >
                    {isConfirming ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Wird gespeichert...
                        </>
                    ) : (
                        <>
                            <IconCheck size={18} className="me-1" />
                            Bestätigen & Speichern
                        </>
                    )}
                </Button>
                </div>
            ) : isSaved ? (
                <div className="org-preview-actions">
                    <Button variant="success" disabled>
                        <IconCheck size={18} className="me-1" />
                        Organisationsstruktur gespeichert
                    </Button>
                </div>
            ) : null}
        </div>
    );
};

/**
 * Wrapper component that provides ReactFlowProvider context
 * Required for proper ReactFlow functionality with hooks
 */
const OrgPreviewChartWithProvider = (props) => {
    return (
        <ReactFlowProvider>
            <OrgPreviewChart {...props} />
        </ReactFlowProvider>
    );
};

export default OrgPreviewChartWithProvider;
